import type { MapData, PointOfInterest } from "@mappedin/mappedin-js";

export type AmenityCategory =
  | "restroom"
  | "food"
  | "info"
  | "exit"
  | "water"
  | "atm"
  | "first-aid"
  | "elevator"
  | "other";

export interface POIEntry {
  id: string;
  name: string;
  description: string;
  category: AmenityCategory;
  poi: PointOfInterest;
}

const CATEGORY_KEYWORDS: Record<AmenityCategory, string[]> = {
  restroom: ["restroom", "toilet", "bathroom", "wc", "lavatory", "men", "women", "accessible restroom"],
  food: ["food", "cafe", "coffee", "restaurant", "dining", "snack", "beverage", "drink", "kiosk"],
  info: ["information", "info", "help desk", "information desk", "reception", "enquiry"],
  exit: ["exit", "emergency exit", "fire exit", "way out"],
  water: ["water", "water fountain", "water station", "drinking water"],
  atm: ["atm", "cash machine", "bank"],
  "first-aid": ["first aid", "medical", "nurse", "health station"],
  elevator: ["elevator", "lift", "escalator", "accessible route"],
  other: [],
};

const CATEGORY_LABELS: Record<AmenityCategory, string> = {
  restroom: "Restrooms",
  food: "Food & Drink",
  info: "Information",
  exit: "Exits",
  water: "Water",
  atm: "ATM",
  "first-aid": "First Aid",
  elevator: "Elevators",
  other: "Amenities",
};

const CATEGORY_COLORS: Record<AmenityCategory, string> = {
  restroom: "#7c3aed",
  food: "#ea580c",
  info: "#1a73e8",
  exit: "#dc2626",
  water: "#0891b2",
  atm: "#059669",
  "first-aid": "#dc2626",
  elevator: "#6366f1",
  other: "#6366f1",
};

const CATEGORY_SVGS: Record<AmenityCategory, string> = {
  restroom: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
  food: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>`,
  info: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>`,
  exit: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>`,
  water: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>`,
  atm: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>`,
  "first-aid": `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>`,
  elevator: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="7 13 12 18 17 13"/><polyline points="7 6 12 11 17 6"/></svg>`,
  other: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
};

function classifyPOI(name: string, description: string): AmenityCategory {
  const combined = `${name} ${description}`.toLowerCase().trim();
  if (!combined) return "other";
  for (const [cat, keywords] of Object.entries(CATEGORY_KEYWORDS) as [AmenityCategory, string[]][]) {
    if (keywords.some((kw) => combined.includes(kw))) return cat;
  }
  return "other";
}

export function buildPOIDirectory(mapData: MapData): POIEntry[] {
  const pois = mapData.getByType("point-of-interest");
  let unnamedCounter = 0;
  return pois
    .map((poi) => ({
      id: poi.id,
      name: poi.name.trim() || `Amenity ${++unnamedCounter}`,
      description: poi.description,
      category: classifyPOI(poi.name, poi.description),
      poi,
    }))
    .sort((a, b) => {
      const catCmp = a.category.localeCompare(b.category);
      return catCmp !== 0 ? catCmp : a.name.localeCompare(b.name);
    });
}

export function poiCategories(entries: POIEntry[]): AmenityCategory[] {
  const seen = new Set<AmenityCategory>();
  for (const e of entries) seen.add(e.category);
  return [...seen];
}

export function filterByPOICategory(entries: POIEntry[], category: AmenityCategory): POIEntry[] {
  return entries.filter((e) => e.category === category);
}

export function getCategoryLabel(cat: AmenityCategory): string {
  return CATEGORY_LABELS[cat];
}

export function getCategoryColor(cat: AmenityCategory): string {
  return CATEGORY_COLORS[cat];
}

export function getCategorySVG(cat: AmenityCategory): string {
  return CATEGORY_SVGS[cat];
}
